import { doc, updateDoc, deleteDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { toast } from 'sonner';

interface PhoneUpdateResult {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Comprehensive phone number update that handles both user collection and phones collection
 * @param userId - The user ID
 * @param oldPhone - The current phone number (to remove from phones collection)
 * @param newPhone - The new phone number (to add to phones collection)
 * @returns Promise<PhoneUpdateResult>
 */
export async function updateUserPhoneComprehensive(
  userId: string,
  oldPhone: string,
  newPhone: string
): Promise<PhoneUpdateResult> {
  try {
    // Validate phone format
    if (!/^05\d{8}$/.test(newPhone)) {
      return {
        success: false,
        message: 'Invalid phone format',
        error: 'Phone number must be in format 05XXXXXXXX (10 digits starting with 05)'
      };
    }

    // Check if new phone number is already reserved by another user
    const newPhoneRef = doc(db, 'phones', newPhone);
    const newPhoneDoc = await getDoc(newPhoneRef);
    
    if (newPhoneDoc.exists()) {
      const existingUserId = newPhoneDoc.data()?.userId;
      if (existingUserId && existingUserId !== userId) {
        return {
          success: false,
          message: 'Phone number already in use',
          error: 'This phone number is already registered to another user'
        };
      }
    }

    // Update user document
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      phone: newPhone,
      updatedAt: new Date()
    });

    // Remove old phone from phones collection
    if (oldPhone && oldPhone !== newPhone) {
      const oldPhoneRef = doc(db, 'phones', oldPhone);
      const oldPhoneDoc = await getDoc(oldPhoneRef);
      
      if (oldPhoneDoc.exists()) {
        const oldPhoneData = oldPhoneDoc.data();
        // Only delete if this phone belongs to the current user
        if (oldPhoneData?.userId === userId) {
          await deleteDoc(oldPhoneRef);
        }
      }
    }

    // Add new phone to phones collection
    await setDoc(newPhoneRef, {
      reserved: true,
      userId: userId,
      reservedAt: new Date()
    });

    return {
      success: true,
      message: 'Phone number updated successfully across all collections'
    };

  } catch (error) {
    console.error('Error updating phone number:', error);
    return {
      success: false,
      message: 'Failed to update phone number',
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Reserve a phone number in the phones collection
 * @param phone - The phone number to reserve
 * @param userId - The user ID to associate with the phone
 */
export async function reservePhone(phone: string, userId: string): Promise<void> {
  const phoneRef = doc(db, 'phones', phone);
  await setDoc(phoneRef, {
    reserved: true,
    userId: userId,
    reservedAt: new Date()
  });
}

/**
 * Release a phone number from the phones collection
 * @param phone - The phone number to release
 * @param userId - The user ID to verify ownership
 */
export async function releasePhone(phone: string, userId: string): Promise<void> {
  const phoneRef = doc(db, 'phones', phone);
  const phoneDoc = await getDoc(phoneRef);
  
  if (phoneDoc.exists()) {
    const phoneData = phoneDoc.data();
    // Only delete if this phone belongs to the specified user
    if (phoneData?.userId === userId) {
      await deleteDoc(phoneRef);
    }
  }
}

/**
 * Check if a phone number is available
 * @param phone - The phone number to check
 * @returns Promise<boolean> - true if available, false if already reserved
 */
export async function isPhoneAvailable(phone: string): Promise<boolean> {
  const phoneRef = doc(db, 'phones', phone);
  const phoneDoc = await getDoc(phoneRef);
  return !phoneDoc.exists();
}
